import { FC, useState } from 'react'
import { ThemeProvider } from 'styled-components/native'
import { BorderRadiusProps, ColorProps, FontSizeProps, FontWeightProps, SpaceProps } from 'styled-system'
import { BlueTheme } from './BlueTheme'
import { dark } from './DarkTheme'
import {
  defaultBorderWidths,
  defaultColors,
  defaultFonts,
  defaultFontSizes,
  defaultFontWeights,
  defaultOpacities,
  defaultRadii,
  defaultSizes,
  defaultSpace,
  defaultTheme
} from './DefaultTheme'
import { GreenTheme } from './GreenTheme'
import { MarigoldTheme } from './MarigoldTheme'
import { MaroonTheme } from './MaroonTheme'
import { MintTheme } from './MintTheme'
import { NaviTheme } from './NaviTheme'
import { newTheme } from './NewTheme'
import { newThemeDark } from './NewThemeDark'
import { PinkTheme } from './PinkTheme'
import { RedTheme } from './RedTheme'

export type LineHeightOptions = keyof typeof newTheme.lineHeights
export const LineHeightOptionsArray = Object.keys(newTheme.lineHeights)
export type ColorOptions = keyof typeof defaultColors
export const ColorOptionsArray = Object.keys(defaultColors)
export type FontSizeOptions = keyof typeof defaultFontSizes
export const FontSizeOptionsArray = Object.keys(defaultFontSizes)
export type FontWeightOptions = keyof typeof defaultFontWeights
export const FontWeightOptionsArray = Object.keys(defaultFontWeights)
export type SpaceOptions = keyof typeof defaultSpace
export const SpaceOptionsArray = Object.keys(defaultSpace)
export type SizeOptions = keyof typeof defaultSizes
export const SizeOptionsArray = Object.keys(defaultSizes)
export type BorderWidthOptions = keyof typeof defaultBorderWidths
export const BorderWidthOptionsArray = Object.keys(defaultBorderWidths)
export type RadiiOptions = keyof typeof defaultRadii
export const RadiiOptionsArray = Object.keys(defaultRadii)
export type OpacityOptions = keyof typeof defaultOpacities
export const OpacityOptionsArray = Object.keys(defaultOpacities)
export type Theme = typeof defaultTheme
export type NewTheme = typeof newTheme
export type FontOptions = keyof typeof defaultFonts
export const FontOptionsArray = Object.keys(defaultFonts)

type ColorType = {
  [colorKeys in keyof ColorProps]?: keyof typeof defaultColors
}

type FontSizeType = {
  [fontSizeProps in keyof FontSizeProps]?: keyof typeof defaultFontSizes | number
}

type FontWeightType = {
  [fontWeighProps in keyof FontWeightProps]?: keyof typeof defaultFontWeights
}

type SpacePropsType = {
  [spaceProps in keyof SpaceProps]?: keyof typeof defaultSpace | number
}

type RadiiPropsType = {
  [radiiProps in keyof BorderRadiusProps]?: keyof typeof defaultRadii | number
}

export interface ThemeProps extends ColorType, FontSizeType, FontWeightType, SpacePropsType, RadiiPropsType { }

export let themes = {
  light: {
    name: 'light',
    ...defaultTheme,
  } as Theme,
  dark: {
    name: 'dark',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...dark.colors },
  } as Theme,
  newThemeLight: {
    name: 'NewLight',
    ...defaultTheme,
    space: { ...defaultTheme.space, ...newTheme.space },
  } as Theme,
  newThemeDark: {
    name: 'NewDark',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...newThemeDark.colors },
    space: { ...defaultTheme.space, ...newTheme.space },
  } as Theme,
  naviTheme: {
    name: 'Navi',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...NaviTheme.colors },
    space: { ...defaultTheme.space, ...NaviTheme.space },
  } as Theme,
  maroonTheme: {
    name: 'Maroon',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...MaroonTheme.colors },
    space: { ...defaultTheme.space, ...MaroonTheme.space },
  } as Theme,
  blueTheme: {
    name: 'Blue',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...BlueTheme.colors },
    space: { ...defaultTheme.space, ...BlueTheme.space },
  } as Theme,
  redTheme: {
    name: 'Red',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...RedTheme.colors },
    space: { ...defaultTheme.space, ...RedTheme.space },
  } as Theme,
  marigoldTheme: {
    name: 'Marigold',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...MarigoldTheme.colors },
    space: { ...defaultTheme.space, ...MarigoldTheme.space },
  } as Theme,
  greenTheme: {
    name: 'Green',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...GreenTheme.colors },
    space: { ...defaultTheme.space, ...GreenTheme.space },
  } as Theme,
  mintTheme: {
    name: 'Mint',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...MintTheme.colors },
    space: { ...defaultTheme.space, ...MintTheme.space },
  } as Theme,
  pinkTheme: {
    name: 'Pink',
    ...defaultTheme,
    colors: { ...defaultTheme.colors, ...PinkTheme.colors },
    space: { ...defaultTheme.space, ...PinkTheme.space },
  } as Theme,
}

interface CustomTheme {
  extends: string
  extraTheme: any
  name: string
}

interface UiThemeProviderProps {
  initialThemeName?: string
  customTheme?: CustomTheme
  children?: any
}

export const UiThemeProvider: FC<UiThemeProviderProps> = ({ children, initialThemeName = 'light', customTheme }) => {
  const [themeName, setTheme] = useState(initialThemeName)
  if (customTheme) {
    const { extraTheme, name } = customTheme
    if (name !== themeName) {
      let currentTheme = { ...(themes[customTheme.extends as keyof typeof themes] ?? themes.light) }
      if (!Object.keys(themes).includes(name)) {
        Object.keys(extraTheme).forEach((key) => {
          currentTheme = {
            ...currentTheme,
            [key]: { ...(currentTheme[key as keyof typeof currentTheme] ?? {}), ...extraTheme[key] },
          }
        })
        themes = { ...themes, [name]: { ...currentTheme, name } }
      }
      setTheme(name)
    }
  }
  return (
    <ThemeProvider theme={{ ...themes[themeName as keyof typeof themes], setTheme, themeName }}>
      {children}
    </ThemeProvider>
  )
}
